// Scene-adapter helpers shared by the pane section builders. The builders
// themselves are pure UI; these functions are the single place where pane
// controls reach into `viewer.objects` (contour meshes, volume uniforms, field
// arrays) — the orchestrator/adapter seam (→ webpic `render/adapters/`).
// Defined once and consumed by both the boot build and the rebuild path so the
// two stay in lockstep.

import * as THREE from 'three';
import type { ViewerContext } from '@app/context';
import { chooseFieldScale } from '@ui/controls/autoScale';
import type { FieldScale } from '@ui/controls/autoScale';

/** Flat array backing the active field component (magnitude → gridFlat). */
export function currentFieldArray(viewer: ViewerContext): Float32Array | null | undefined {
    return (viewer.field.currentComponent === 'magnitude')
        ? viewer.field.gridFlat
        : (viewer.field.data ? viewer.field.data[viewer.field.currentComponent] : null);
}

/** Apply `fn` to every contour mesh's (first) standard material, flagging it
 *  for re-upload. No-op when no contour meshes exist. */
export function forEachContourMaterial(viewer: ViewerContext, fn: (m: THREE.MeshStandardMaterial) => void): void {
    viewer.objects.contours.meshes.forEach((m) => {
        if (!m || !m.material) return;
        const mat = Array.isArray(m.material) ? m.material[0] : m.material;
        if (!mat) return;
        fn(mat as THREE.MeshStandardMaterial);
        mat.needsUpdate = true;
    });
}

// One O(n) min/max scan — the only data the scale chooser needs (see
// chooseFieldScale; no distribution sort). Memoize on array identity (mirrors
// globalStatsMemo in ../selections/stats.ts) so the contour pane's rebuilds
// (selection add/relabel) don't rescan; the reference changes on dataset /
// component switch, invalidating naturally.
let rangeMemo: { arr: Float32Array; min: number; max: number; maxAbs: number } | null = null;
function fieldRange(viewer: ViewerContext): { min: number; max: number; maxAbs: number } | null {
    const arr = currentFieldArray(viewer);
    if (!arr || !arr.length) return null;
    if (rangeMemo && rangeMemo.arr === arr) return rangeMemo;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < arr.length; i++) {
        const v = arr[i] ?? 0;
        if (v < min) min = v;
        if (v > max) max = v;
    }
    const maxAbs = Math.max(Math.abs(min), Math.abs(max));
    rangeMemo = { arr, min, max, maxAbs };
    return rangeMemo;
}

/** Padded [min, max] of the active field plus the scale the contour-value slider
 *  should use, chosen from the field's range (see chooseFieldScale): linear for a
 *  narrow positive field, `log` for a wide clean positive one, else `symlog` with
 *  the linear band pinned to the innermost power of ten near 0 (±10 for dipole
 *  Bz). Falls back to [-1, 1] / symlog when no field is loaded. */
export function getContourRange(viewer: ViewerContext): { min: number; max: number } & FieldScale {
    const r = fieldRange(viewer);
    if (!r) return { min: -1, max: 1, kind: 'symlog', linthresh: 1, origin: 0 };
    const pad = (r.max - r.min) * 0.02;
    const scale = chooseFieldScale({ min: r.min, max: r.max, maxAbs: r.maxAbs });
    return { min: r.min + pad, max: r.max - pad, ...scale };
}

/** Write a scalar uniform on the volume raymarch material, if present. */
export function setVolumeUniform(viewer: ViewerContext, key: string, val: number): void {
    const mat = viewer.objects.volume.material as THREE.ShaderMaterial | null;
    const u = mat ? mat.uniforms : null;
    if (u && u[key]) u[key].value = val;
}
